import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import SavedSearchCard, { formatSearchSummary } from 'ui/saved-search';
import LoadingSpinner from 'components/loading-spinner';
import PaginationNav from 'components/control/pagination-nav';
import { useSavedSearchStore } from 'hooks/use-saved-search-store';
import { AlertFrequency } from 'domain/saved-search';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { Namespaces, LOCALE_SEARCH_PATHS as P } from 'types/locales';
import { newListingsBadgeLabel } from './new-listings-badge-label';
import { useClientPagination } from './use-client-pagination';
import styles from './collections-saved-searches.module.scss';

/**
 * One capped list is fetched in a single pass; the grid then pages through it
 * client-side (see {@link useClientPagination}), so the store stays limit-based.
 * Exported so the eager tab-count hook shares the same React Query cache key.
 */
export const SAVED_SEARCHES_FETCH_LIMIT = 100;


export type CollectionsSavedSearchesProps = {
  /** True while this tab is the visible one — gates the fetch. */
  isActive: boolean;
  /** Applies a saved search to the page. */
  onApplySavedSearch: (filter: Filter) => void;
};

/**
 * Saved Searches tab of the Collections pane: a two-column grid of the same
 * `SavedSearchCard` the area page's saved-search panel uses, listing every
 * saved search rather than the panel's most-recent three. Rename, delete, and
 * alert-frequency edits go through the same store as the panel, so both stay in
 * sync. Clicking a card applies that search, which for a search saved on
 * another kind of page means leaving for the page it applies to; the page's own
 * handler decides (see `SavedSearchPolicy.isAreaSearch`).
 */
export function CollectionsSavedSearches({ isActive, onApplySavedSearch }: CollectionsSavedSearchesProps) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const {
    recents: savedSearches,
    isLoading,
    fetchRecents,
    updateFrequency,
    editSavedSearch,
    removeSavedSearch,
  } = useSavedSearchStore({ limit: SAVED_SEARCHES_FETCH_LIMIT });
  const { pageItems, pageNumber, totalPages, setPageNumber } = useClientPagination(savedSearches);

  useEffect(() => {
    if (isActive) void fetchRecents();
  }, [isActive, fetchRecents]);

  const frequencyOptions = [
    { value: '0', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_NO_ALERTS, { defaultValue: 'No alerts' }) },
    { value: '-1', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_INSTANT, { defaultValue: 'Instant' }) },
    { value: '86400', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_DAILY, { defaultValue: 'Daily' }) },
    { value: '604800', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_WEEKLY, { defaultValue: 'Weekly' }) },
  ];

  const cardLabels = {
    options: t(P.SAVED_SEARCH_PANEL_OPTIONS, { defaultValue: 'Saved search options' }),
    rename: t(P.SAVED_SEARCH_PANEL_RENAME, { defaultValue: 'Rename' }),
    delete: t(P.SAVED_SEARCH_PANEL_DELETE, { defaultValue: 'Delete' }),
    frequency: t(P.SAVED_SEARCH_PANEL_FREQUENCY_LABEL, { defaultValue: 'Alert frequency' }),
    confirmRename: t(P.SAVED_SEARCH_PANEL_CONFIRM_RENAME, { defaultValue: 'Finish rename' }),
    unselect: t(P.SAVED_SEARCH_PANEL_UNSELECT, { defaultValue: 'Unselect' }),
  };

  if (isLoading && savedSearches.length === 0) {
    return (
      <div className={styles.state}>
        <LoadingSpinner />
      </div>
    );
  }

  if (savedSearches.length === 0) {
    return (
      <p className={styles.empty}>
        {t(P.SAVED_SEARCH_PANEL_EMPTY, { defaultValue: 'No saved searches yet.' })}
      </p>
    );
  }

  return (
    <>
      <div className={styles.grid} data-testid="AreaPageV2-Collections-SavedSearchesGrid">
        {pageItems.map(record => (
          <div key={record.id} className={styles.cell}>
            <SavedSearchCard
              title={record.description}
              summary={formatSearchSummary(record.summary, t)}
              labels={cardLabels}
              imageUrl={record.imageUrl}
              newListingsLabel={newListingsBadgeLabel(record.newListingsCount, t)}
              frequencyValue={String(record.frequency.seconds)}
              frequencyOptions={frequencyOptions}
              onSelect={() => onApplySavedSearch(record.filter)}
              onFrequencyChange={value => {
                void updateFrequency(record, AlertFrequency.from(Number(value)));
              }}
              onRename={name => {
                void editSavedSearch(record, { name });
              }}
              onDelete={() => {
                void removeSavedSearch(record);
              }}
            />
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <PaginationNav pageNumber={pageNumber} totalPages={totalPages} setPageNumber={setPageNumber} />
        </div>
      )}
    </>
  );
}
